import { tw } from "@careinfo/tailwind";
import * as Prism from "prismjs";
import "./style.css";
import { useEffect, useRef } from "react";
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from "react-hook-form";

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  icon?: React.ReactElement;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  language?: string;
}

export default function <T extends FieldValues>({
  control,
  name,
  label,
  language = "html",
}: Props<T>) {
  const {
    field: { onChange, value = "" },
  } = useController({
    control,
    name,
  });

  const editorRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null); // Ref for the textarea

  const syncScroll = () => {
    if (textareaRef.current && editorRef.current) {
      editorRef.current.scrollTop = textareaRef.current.scrollTop;
      editorRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const syncSize = () => {
    if (textareaRef.current && editorRef.current) {
      editorRef.current.style.height = textareaRef.current.style.height;
    }
  };

  useEffect(() => {
    document.addEventListener("resize", () => {
      syncScroll();
      syncSize();
    });
    return () => {
      document.removeEventListener("resize", () => {
        syncScroll();
        syncSize();
      });
    };
  }, []);
  const grammar = Prism.languages?.[language];

  let highlightedCode: string;
  if (grammar && typeof value === "string") {
    try {
      highlightedCode = Prism.highlight(value, grammar, language);
    } catch (e) {
      console.error("Prism highlight error:", e);
      highlightedCode = value;
    }
  } else {
    console.warn(`Unsupported language or null input: "${language}"`);
    highlightedCode = typeof value === "string" ? value : "";
  }
  return (
    <div>
      <label htmlFor={name}>{label}</label>
      <div
        className={tw`relative w-full h-[650px] resize-y overflow-y-auto`}
        onResetCapture={syncSize}
      >
        <div
          ref={editorRef}
          className={tw`absolute inset-0 p-[1rem] pointer-events-none`}
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            background: "#2d2d2d",
            color: "#f8f8f2",
            overflow: "auto",
          }}
          dangerouslySetInnerHTML={{
            __html: highlightedCode,
          }}
        />
        <textarea
          id={name}
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={syncScroll}
          className={tw`absolute inset-0 w-full h-full bg-transparent text-transparent focus:outline-none p-[1rem] caret-white`}
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            overflow: "auto",
            caretColor: "white",
          }}
          spellCheck="false"
        />
      </div>
    </div>
  );
}
